<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import Avatar from 'next/avatar/Avatar.vue';
import staffAiAPI from 'dashboard/api/nexceo/staffAiConversations';

const store = useStore();
const { accountId } = useAccount();
const { isAdmin } = useAdmin();
const currentUser = computed(() => store.getters['auth/getCurrentUser']);

// ── Conversation list ─────────────────────────────────────────────────────
const conversations = computed(
  () => store.getters['nexceoStaffAiConversations/allConversations']
);
const listLoading = computed(
  () => store.getters['nexceoStaffAiConversations/loading']
);
const searchQuery = ref('');
const filterProject = ref('');

const filteredList = computed(() => {
  let list = conversations.value;
  if (filterProject.value)
    list = list.filter(c => c.project?.id === Number(filterProject.value));
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter(
      c =>
        c.team_member?.display_name?.toLowerCase().includes(q) ||
        c.phone_number?.includes(q) ||
        c.project?.name?.toLowerCase().includes(q)
    );
  }
  return list;
});

const projectOptions = computed(() => {
  const seen = new Set();
  return conversations.value
    .filter(c => c.project && !seen.has(c.project.id) && seen.add(c.project.id))
    .map(c => ({ id: c.project.id, name: c.project.name }));
});

const myConversation = computed(() => {
  const uid = currentUser.value?.id;
  const email = currentUser.value?.email;
  return (
    conversations.value.find(c => uid && c.team_member?.user_id === uid) ||
    conversations.value.find(
      c => email && c.team_member?.contact_email === email
    ) ||
    null
  );
});

// ── Active conversation + messages ────────────────────────────────────────
const selectedConv = ref(null);
const messages = ref([]);
const msgLoading = ref(false);
const msgError = ref('');
const messagesEl = ref(null);

// ── Send ──────────────────────────────────────────────────────────────────
const composerText = ref('');
const sending = ref(false);
const sendError = ref('');

async function scrollToBottom() {
  await nextTick();
  if (messagesEl.value)
    messagesEl.value.scrollTop = messagesEl.value.scrollHeight;
}

async function selectConversation(conv) {
  if (!conv?.connected || selectedConv.value?.id === conv.id) return;
  selectedConv.value = conv;
  messages.value = [];
  msgError.value = '';
  sendError.value = '';
  msgLoading.value = true;
  try {
    const { data } = await staffAiAPI.getMessages(conv.id);
    messages.value = data.messages || [];
    await scrollToBottom();
  } catch (e) {
    msgError.value = e?.response?.data?.error || 'Failed to load messages.';
  } finally {
    msgLoading.value = false;
  }
}

async function sendMessage() {
  const content = composerText.value.trim();
  if (!content || !selectedConv.value || sending.value) return;
  sending.value = true;
  sendError.value = '';
  composerText.value = '';
  try {
    const { data } = await staffAiAPI.sendMessage(
      selectedConv.value.id,
      content
    );
    if (data.message) messages.value.push(data.message);
    if (data.ai_response) messages.value.push(data.ai_response);
    await scrollToBottom();
  } catch (e) {
    sendError.value = e?.response?.data?.error || 'Failed to send.';
    composerText.value = content;
  } finally {
    sending.value = false;
  }
}

function onKeydown(e) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    sendMessage();
  }
}

function sendChip(text) {
  composerText.value = text;
  sendMessage();
}

const QUICK_CHIPS = [
  "Today's leads",
  'Hot leads',
  'Upcoming visits',
  'Summary',
  'Help',
];

// ── Repair ────────────────────────────────────────────────────────────────
const isRepairing = ref(false);
const repairMsg = ref('');

async function onRepair() {
  isRepairing.value = true;
  repairMsg.value = '';
  try {
    const { data } = await staffAiAPI.repair();
    repairMsg.value = data.message;
    store.dispatch('nexceoStaffAiConversations/fetchConversations');
  } catch (e) {
    repairMsg.value = e?.response?.data?.error || 'Repair failed.';
  } finally {
    isRepairing.value = false;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────
function nexceoUrl(conv) {
  if (!conv?.conversation_id) return null;
  return `/app/accounts/${accountId.value}/conversations/${conv.conversation_id}`;
}

function isAiMsg(msg) {
  return msg.is_ai || msg.sender_type === 'ai';
}

// Incoming from staff WhatsApp = LEFT; web UI outgoing = RIGHT; AI = LEFT
function isRightAligned(msg) {
  return !isAiMsg(msg) && (msg.is_staff_web || msg.message_type === 1);
}

function bubbleClass(msg) {
  if (isAiMsg(msg))
    return 'bg-n-solid-iris text-n-slate-12 rounded-xl ltr:rounded-bl-sm';
  if (isRightAligned(msg))
    return 'bg-n-solid-blue text-n-slate-12 rounded-xl ltr:rounded-br-sm';
  return 'bg-n-slate-4 text-n-slate-12 rounded-xl ltr:rounded-bl-sm';
}

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function relativeTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// ── Init ──────────────────────────────────────────────────────────────────
onMounted(async () => {
  await store.dispatch('nexceoStaffAiConversations/fetchConversations');
  if (!isAdmin.value && myConversation.value?.connected)
    selectConversation(myConversation.value);
});

watch(myConversation, conv => {
  if (!isAdmin.value && conv?.connected && !selectedConv.value)
    selectConversation(conv);
});
</script>

<template>
  <div
    class="flex w-full h-full overflow-hidden bg-n-background dark:bg-n-solid-3"
  >
    <!-- ══════════════════ LEFT: CONVERSATION LIST ══════════════════════ -->
    <aside
      class="flex flex-col w-[22rem] shrink-0 border-r border-n-slate-3 dark:border-n-strong bg-n-background dark:bg-n-solid-2 overflow-hidden"
    >
      <!-- List header -->
      <div
        class="flex items-center justify-between h-12 px-4 border-b border-n-slate-3 dark:border-n-strong shrink-0 bg-n-background dark:bg-n-solid-2"
      >
        <div class="flex items-center gap-2">
          <span class="i-lucide-bot-message-square size-4 text-purple-500" />
          <h3 class="text-sm font-medium text-n-slate-12">NEXA Staff AI</h3>
          <span
            v-tooltip.right="
              'NEXA is your internal team assistant for project managers and executives. It helps with assigned leads, follow-ups, site visits, and project summaries — via WhatsApp or this web panel. Internal only — never visible to customers.'
            "
            class="i-lucide-info size-3.5 text-n-slate-7 cursor-help"
          />
        </div>
        <div class="flex items-center gap-0.5">
          <button
            v-if="isAdmin"
            v-tooltip.bottom="'Repair all Staff AI conversations'"
            :disabled="isRepairing"
            class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors disabled:opacity-40"
            @click="onRepair"
          >
            <span
              :class="
                isRepairing
                  ? 'i-lucide-loader-2 size-4 animate-spin'
                  : 'i-lucide-wrench size-4'
              "
            />
          </button>
          <button
            v-tooltip.bottom="'Refresh'"
            class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click="
              store.dispatch('nexceoStaffAiConversations/fetchConversations')
            "
          >
            <span class="i-lucide-refresh-cw size-4" />
          </button>
        </div>
      </div>

      <!-- Repair banner -->
      <div
        v-if="repairMsg"
        class="mx-3 mt-2 px-3 py-1.5 rounded-lg text-xs text-n-slate-11 bg-n-alpha-1 border border-n-weak shrink-0"
      >
        {{ repairMsg }}
      </div>

      <!-- Search + filter (admin) -->
      <div
        v-if="isAdmin"
        class="px-3 py-2 border-b border-n-slate-3 dark:border-n-strong shrink-0"
      >
        <div class="relative">
          <span
            class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-search size-3 text-n-slate-9 pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search staff, phone, project…"
            class="w-full pl-7 pr-3 py-1.5 text-sm rounded-lg border border-n-weak dark:border-n-strong bg-transparent text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-woot-500 focus:ring-0 transition-colors"
          />
        </div>
        <select
          v-if="projectOptions.length > 1"
          v-model="filterProject"
          class="mt-1.5 w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak dark:border-n-strong bg-white dark:bg-n-solid-3 text-n-slate-12 focus:outline-none focus:border-woot-500"
        >
          <option value="">All Projects</option>
          <option v-for="p in projectOptions" :key="p.id" :value="p.id">
            {{ p.name }}
          </option>
        </select>
      </div>

      <!-- Conversation items -->
      <div class="flex-1 overflow-y-auto min-h-0">
        <!-- Loading -->
        <div v-if="listLoading" class="flex items-center justify-center py-10">
          <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-9" />
        </div>

        <!-- Items -->
        <template v-else-if="filteredList.length">
          <div
            v-for="conv in filteredList"
            :key="conv.id"
            class="flex w-full gap-3 px-3 py-4 transition-all duration-300 ease-in-out border-b border-n-slate-3 dark:border-n-strong"
            :class="[
              conv.connected
                ? 'cursor-pointer'
                : 'cursor-not-allowed opacity-50',
              selectedConv?.id === conv.id
                ? 'bg-n-alpha-1 dark:bg-n-alpha-3'
                : conv.connected
                  ? 'hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2'
                  : '',
            ]"
            @click="conv.connected && selectConversation(conv)"
          >
            <!-- Avatar -->
            <Avatar
              :name="conv.team_member?.display_name || '?'"
              :size="24"
              rounded-full
              class="flex-shrink-0"
            />

            <!-- Content -->
            <div class="flex flex-col w-full gap-1 min-w-0">
              <!-- Name + time row -->
              <div class="flex items-center justify-between h-6 gap-2">
                <h4 class="text-base font-medium truncate text-n-slate-12">
                  {{ conv.team_member?.display_name || '—' }}
                </h4>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <span
                    v-tooltip.left="
                      conv.connected ? 'Staff AI Connected' : 'Not connected'
                    "
                    class="inline-flex items-center justify-center flex-shrink-0 rounded-full size-5"
                    :class="[
                      conv.connected
                        ? 'bg-success-100 dark:bg-success-900/30'
                        : 'bg-n-alpha-2',
                    ]"
                  >
                    <span
                      class="size-1.5 rounded-full"
                      :class="[
                        conv.connected ? 'bg-success-500' : 'bg-n-slate-7',
                      ]"
                    />
                  </span>
                  <span class="text-sm text-n-slate-10">
                    {{ relativeTime(conv.last_synced_at) }}
                  </span>
                </div>
              </div>
              <!-- Project + role preview -->
              <p class="text-sm text-n-slate-11 truncate leading-tight">
                {{ conv.project?.name || '—' }}
              </p>
              <p class="text-xs text-n-slate-9 truncate leading-tight">
                {{ conv.team_member?.role_label || '—' }}
                <span v-if="conv.phone_number" class="font-mono">
                  &nbsp;·&nbsp;{{ conv.phone_number }}
                </span>
              </p>
            </div>
          </div>
        </template>

        <!-- Empty state -->
        <div
          v-else
          class="flex flex-col items-center justify-center gap-2 py-16 px-6 text-center"
        >
          <span
            class="i-lucide-message-square-dashed size-8 text-n-slate-7 opacity-60"
          />
          <p class="text-sm text-n-slate-11 leading-5">
            <template v-if="isAdmin">
              No NEXA conversations found.<br />
              First add team members with WhatsApp numbers in
              <strong>Projects → Team Members</strong>, then click
              <button
                class="text-woot-500 hover:underline text-sm font-medium"
                @click="onRepair"
              >
                Repair All</button
              >.
            </template>
            <template v-else>
              Your NEXA Staff AI chat is not set up.<br />
              Ask admin to add your WhatsApp in<br />
              <strong>Project → Team Members</strong>.
            </template>
          </p>
        </div>
      </div>
    </aside>

    <!-- ══════════════════ RIGHT: CHAT PANEL ════════════════════════════ -->
    <section
      class="flex-1 flex flex-col min-w-0 overflow-hidden bg-n-surface-1"
    >
      <!-- ── Empty state: no conversation selected ── -->
      <div
        v-if="!selectedConv"
        class="flex-1 flex flex-col items-center justify-center gap-4 text-center px-8"
      >
        <span class="i-lucide-bot size-12 text-n-slate-7 opacity-30" />
        <div>
          <p class="text-sm font-semibold text-n-slate-11">
            {{
              filteredList.length
                ? 'Select a NEXA conversation'
                : 'No NEXA Staff AI conversations yet'
            }}
          </p>
          <p class="text-xs text-n-slate-9 mt-1 max-w-xs leading-relaxed">
            {{
              filteredList.length
                ? 'Choose a team member from the left to view their NEXA conversation.'
                : 'Add team members with WhatsApp numbers in Projects → Team Members, then click Repair All.'
            }}
          </p>
        </div>
        <template v-if="filteredList.length">
          <div class="w-full max-w-xs">
            <p class="text-xs text-n-slate-8 font-medium mb-2">
              Example questions:
            </p>
            <div class="flex flex-wrap gap-1.5 justify-center">
              <span
                v-for="ex in [
                  'Today\'s leads',
                  'Hot leads',
                  'Upcoming visits',
                  'Project summary',
                  'Status of [name]',
                ]"
                :key="ex"
                class="text-xs px-2.5 py-1 rounded-full border border-n-weak text-n-slate-10 bg-n-alpha-1"
                >{{ ex }}</span
              >
            </div>
            <p class="text-xs text-n-slate-7 mt-2">
              These also work via WhatsApp.
            </p>
          </div>
        </template>
      </div>

      <!-- ── Active conversation ── -->
      <template v-else>
        <!-- Header — matches ConversationHeader height h-12 -->
        <header
          class="flex items-center justify-between px-4 h-16 border-b border-n-slate-3 dark:border-n-strong bg-n-background dark:bg-n-solid-2 shrink-0"
        >
          <div
            class="flex items-center justify-start max-w-full min-w-0 flex-1 gap-0"
          >
            <!-- Avatar -->
            <Avatar
              :name="selectedConv.team_member?.display_name || '?'"
              :size="32"
              rounded-full
              class="flex-shrink-0"
            />
            <!-- Contact info -->
            <div
              class="flex flex-col items-start min-w-0 ltr:ml-2 rtl:mr-2 overflow-hidden"
            >
              <div class="flex flex-row items-center max-w-full gap-1 m-0">
                <h3
                  class="text-sm font-medium truncate leading-tight text-n-slate-12"
                >
                  {{ selectedConv.team_member?.display_name || '—' }}
                </h3>
                <span
                  class="inline-flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 h-5 px-1.5 text-[10px] font-medium text-n-slate-11"
                >
                  Staff AI
                </span>
              </div>
              <div
                class="flex items-center gap-1 overflow-hidden text-xs text-n-slate-11 text-ellipsis whitespace-nowrap"
              >
                <span class="truncate">{{
                  selectedConv.project?.name || '—'
                }}</span>
                <span class="text-n-slate-7">·</span>
                <span class="truncate">{{
                  selectedConv.team_member?.role_label || '—'
                }}</span>
                <span class="text-n-slate-7">·</span>
                <span class="font-mono text-n-slate-9 truncate">{{
                  selectedConv.phone_number
                }}</span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex items-center gap-2 flex-shrink-0">
            <span
              class="hidden md:flex items-center gap-1 text-[10px] text-n-slate-9 bg-n-alpha-2 px-2 py-1 rounded-full"
            >
              <span class="i-lucide-shield-check size-3 text-n-slate-8" />
              Internal · Never visible to customers
            </span>
            <a
              v-if="nexceoUrl(selectedConv)"
              v-tooltip.bottom="'Open in NEXCEO'"
              :href="nexceoUrl(selectedConv)"
              target="_blank"
              class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            >
              <span class="i-lucide-external-link size-4" />
            </a>
          </div>
        </header>

        <!-- Message list — exact Chatwoot MessageList container -->
        <div
          ref="messagesEl"
          class="flex-1 overflow-y-auto min-h-0 bg-n-surface-1"
        >
          <!-- Loading -->
          <div v-if="msgLoading" class="flex items-center justify-center py-10">
            <span
              class="i-lucide-loader-2 size-5 animate-spin text-n-slate-9"
            />
          </div>

          <!-- Error -->
          <div
            v-else-if="msgError"
            class="flex items-center justify-center gap-2 py-8 text-sm text-ruby-500"
          >
            <span class="i-lucide-alert-circle size-4" />
            {{ msgError }}
          </div>

          <!-- Empty -->
          <div
            v-else-if="!messages.length"
            class="flex flex-col items-center justify-center h-full gap-2 text-center py-12"
          >
            <span class="i-lucide-sparkles size-7 text-n-slate-7 opacity-50" />
            <p class="text-sm text-n-slate-11">No messages yet.</p>
            <p class="text-xs text-n-slate-9">
              Use a quick chip below or type a message.
            </p>
          </div>

          <!-- Messages — px-4 matches MessageList exactly -->
          <ul v-else class="px-4 bg-n-surface-1 py-3 flex flex-col gap-0.5">
            <li
              v-for="msg in messages"
              :key="msg.id"
              class="flex w-full mb-2 message-bubble-container"
              :class="isRightAligned(msg) ? 'justify-end' : 'justify-start'"
            >
              <!-- Left: avatar + bubble -->
              <div
                v-if="!isRightAligned(msg)"
                class="flex items-end gap-2 ltr:mr-8 rtl:ml-8"
              >
                <div class="flex items-end [grid-area:avatar]">
                  <Avatar
                    v-if="isAiMsg(msg)"
                    name="NEXCEO AI"
                    icon-name="i-lucide-bot"
                    :size="24"
                    rounded-full
                    class="flex-shrink-0"
                  />
                  <Avatar
                    v-else
                    :name="selectedConv.team_member?.display_name || '?'"
                    :size="24"
                    rounded-full
                    class="flex-shrink-0"
                  />
                </div>
                <div class="[grid-area:bubble] flex flex-col items-start">
                  <div
                    class="px-4 py-3 max-w-lg text-sm"
                    :class="[bubbleClass(msg)]"
                  >
                    {{ msg.content }}
                  </div>
                  <!-- Meta — exact MessageMeta: text-xs flex items-center gap-1.5 -->
                  <div
                    class="mt-1 text-xs flex items-center gap-1.5 text-n-slate-9 px-1"
                  >
                    <time>{{ formatTime(msg.created_at) }}</time>
                    <span class="text-n-slate-7">·</span>
                    <span v-if="isAiMsg(msg)" class="text-n-slate-9">
                      NEXCEO AI
                    </span>
                    <span v-else class="text-n-slate-9">WhatsApp</span>
                  </div>
                </div>
              </div>

              <!-- Right: bubble + avatar -->
              <div v-else class="flex items-end gap-2 ltr:ml-8 rtl:mr-8">
                <div class="[grid-area:bubble] flex flex-col items-end">
                  <div
                    class="px-4 py-3 max-w-lg text-sm"
                    :class="[bubbleClass(msg)]"
                  >
                    {{ msg.content }}
                  </div>
                  <div
                    class="mt-1 text-xs flex items-center gap-1.5 text-n-slate-9 px-1"
                  >
                    <time>{{ formatTime(msg.created_at) }}</time>
                    <span class="text-n-slate-7">·</span>
                    <span class="text-n-slate-9">Web</span>
                  </div>
                </div>
                <div class="[grid-area:avatar] flex items-end">
                  <Avatar
                    :name="currentUser?.name || 'You'"
                    :size="24"
                    rounded-full
                    class="flex-shrink-0"
                  />
                </div>
              </div>
            </li>

            <!-- Typing indicator -->
            <li v-if="sending" class="flex w-full mb-2 justify-start">
              <div class="flex items-end gap-2 ltr:mr-8">
                <Avatar
                  name="NEXCEO AI"
                  icon-name="i-lucide-bot"
                  :size="24"
                  rounded-full
                  class="flex-shrink-0"
                />
                <div
                  class="px-4 py-3 bg-n-slate-4 text-n-slate-12 rounded-xl ltr:rounded-bl-sm"
                >
                  <div class="flex gap-1 items-center h-4">
                    <span
                      class="size-1.5 rounded-full bg-n-slate-8 animate-bounce [animation-delay:0ms]"
                    />
                    <span
                      class="size-1.5 rounded-full bg-n-slate-8 animate-bounce [animation-delay:150ms]"
                    />
                    <span
                      class="size-1.5 rounded-full bg-n-slate-8 animate-bounce [animation-delay:300ms]"
                    />
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- Quick chips -->
        <div
          class="flex items-center gap-1.5 px-4 py-2 border-t border-n-slate-3 dark:border-n-strong bg-n-background dark:bg-n-solid-2 flex-wrap shrink-0"
        >
          <span class="text-xs text-n-slate-9 mr-0.5">Quick:</span>
          <button
            v-for="chip in QUICK_CHIPS"
            :key="chip"
            :disabled="sending"
            class="text-xs px-2.5 py-1 rounded-full border border-n-weak text-n-slate-11 hover:border-woot-400 hover:text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-500/10 transition-colors disabled:opacity-40"
            @click="sendChip(chip)"
          >
            {{ chip }}
          </button>
        </div>

        <!-- Send error -->
        <div
          v-if="sendError"
          class="px-4 py-1.5 bg-ruby-50 dark:bg-ruby-500/10 border-t border-ruby-100 dark:border-ruby-800 shrink-0"
        >
          <p class="text-xs text-ruby-600 flex items-center gap-1.5">
            <span class="i-lucide-alert-circle size-3" />{{ sendError }}
          </p>
        </div>

        <!-- Composer — exact .reply-box: relative mb-2 mx-2 border border-n-weak rounded-xl bg-n-solid-1 -->
        <div
          class="relative mb-2 mx-2 border border-n-weak dark:border-n-strong rounded-xl bg-n-solid-1 dark:bg-n-solid-2 shrink-0"
        >
          <div class="flex items-end gap-2 px-3 py-2">
            <textarea
              v-model="composerText"
              rows="1"
              placeholder="Ask NEXA… Enter ↵ sends, Shift+Enter for new line"
              :disabled="sending"
              class="flex-1 resize-none text-sm bg-transparent text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none leading-5 max-h-24 overflow-y-auto py-1"
              @keydown="onKeydown"
            />
            <button
              :disabled="!composerText.trim() || sending"
              class="flex items-center justify-center w-8 h-8 rounded-lg bg-woot-500 hover:bg-woot-600 active:bg-woot-700 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              @click="sendMessage"
            >
              <span
                v-if="sending"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span v-else class="i-lucide-send-horizontal size-4" />
            </button>
          </div>
        </div>
      </template>
    </section>
  </div>
</template>
